import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";


import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { API_BASE_URL } from "../../ApiConfig";
import style from "../../../css/dashboard/sector/Sector.module.css";
import { getSecureToken } from "../../ApisService/apiService";


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const generateColors = (count) => {
  const baseColors = [
    "#3C6E71", // Deep Teal (smart, refined)
    "#70A9A1", // Soft Aqua (fresh, clean)
    "#F4D35E", // English Mustard (bright, heritage)
    "#EE964B", // Apricot Orange (warm, classy)
    "#F95738", // Classic Coral (bright, punchy but elegant)
    "#9F7E69", // Mocha Beige (earthy neutral)
    "#8E9AAF", // Dusty Indigo (muted and modern)
    "#E2C2B9", // Blush Pink (soft, editorial)
    "#A3B18A", // Sage Green (calm, premium)
    "#6D6875"  // Dusty Plum (vintage and professional)
  ];


  return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
};

const IndustryChart = () => {
  const [industryData, setIndustryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [trendFilter, setTrendFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [industrySearch, setIndustrySearch] = useState("");


  // useEffect(() => {
  //   axios
  //     .get(`${API_BASE_URL}sector/get_industry_rotation.php`)
  //     .then((res) => {
  //       const formatted = res.data.map((i) => {
  //         const open = parseFloat(i.avg_open) || 0;
  //         const close = parseFloat(i.avg_close) || 0;
  //         return {
  //           ...i,
  //           avgOpen: open,
  //           avgClose: close,
  //           avgHigh: parseFloat(i.avg_high) || 0,
  //           avgLow: parseFloat(i.avg_low) || 0,
  //           changePercentage: open === 0 ? 0 : ((close - open) / open) * 100,
  //           trend: close > open ? "up" : close < open ? "down" : "neutral",
  //         };
  //       });
  //       setIndustryData(formatted);
  //       setFilteredData(formatted);
  //       setLoading(false);
  //     })
  //     .catch(() => {
  //       setError("Unable to fetch data. Please try again later.");
  //       setLoading(false);
  //     });
  // }, []);

 useEffect(() => {
  const fetchData = async () => {
    try {
      const { secrettoken, ts } = await getSecureToken();

      const res = await axios.get(`${API_BASE_URL}sector/get_industry_rotation.php`, {
        headers: {
          'X-TOKEN': secrettoken,
          'X-TIMESTAMP': ts,
        },
        withCredentials: true,
      });

      const formatted = res.data.map((i) => {
        const open = parseFloat(i.avg_open) || 0;
        const close = parseFloat(i.avg_close) || 0;
        return {
          ...i,
          avgOpen: open,
          avgClose: close,
          avgHigh: parseFloat(i.avg_high) || 0,
          avgLow: parseFloat(i.avg_low) || 0,
          changePercentage: open === 0 ? 0 : ((close - open) / open) * 100,
          trend: close > open ? "up" : close < open ? "down" : "neutral",
        };
      });

      // console.log(res.data);
      setIndustryData(formatted);
      setLoading(false);
    } catch (error) {
      // console.error("Error fetching industry rotation:", error);
      setError("Unable to fetch data. Please try again later.");
      setLoading(false);
    }
  };

  fetchData(); // Initial fetch

  const interval = setInterval(() => {
    fetchData();
  }, 30 * 60 * 1000); // 30 minutes

  return () => clearInterval(interval);
}, [trendFilter]);


  useEffect(() => {
    // Every time trendFilter or industryData changes, apply filter
    filterDataByTrend(trendFilter);
  }, [trendFilter, industryData]);


  const filterDataByTrend = (trend) => {
    setTrendFilter(trend);
    let sortedData = [...industryData];

    if (trend === "up") {
      sortedData = sortedData
        .filter((i) => i.trend === "up")
        .sort((a, b) => b.changePercentage - a.changePercentage);
    } else if (trend === "down") {
      sortedData = sortedData
        .filter((i) => i.trend === "down")
        .sort((a, b) => a.changePercentage - b.changePercentage);
    } else if (trend === "neutral") {
      sortedData = sortedData
        .filter((i) => i.trend === "neutral")
        .sort((a, b) => b.changePercentage - a.changePercentage);
    } else {
      sortedData = sortedData.sort((a, b) => b.changePercentage - a.changePercentage);
    }

    setFilteredData(sortedData);
  };


  const formatNumber = (num) => (isNaN(num) ? "N/A" : num.toFixed(2));

  const topChanges = filteredData.slice(0, 5);


  const chartData = {
    labels: filteredData.map((i) => i.industry),
    datasets: [
      {
        label: "Avg Change (%)",
        data: filteredData.map((i) => i.changePercentage),
        backgroundColor: generateColors(filteredData.length),
        borderRadius: 5,
        borderSkipped: false,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    animation: {
      duration: 900,
      easing: "easeOutCubic",
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y.toFixed(2)}%`,
        },
      },
      datalabels: {
        display: (context) => Math.abs(context.dataset.data[context.dataIndex]) > 6,
        color: "#000",
        anchor: 'end',
        align: 'top',
        formatter: (value) => `${value.toFixed(2)}%`,
        font: {
          weight: 'bold',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (val, index) {
            const label = this.getLabelForValue(val);
            return label.length > 10 ? label.slice(0, 10) + "…" : label;
          },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        max: 6,
        ticks: {
          callback: (value) => `${value}%`,
        },
      },


    },
  };

  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [industryStocks, setIndustryStocks] = useState([]);
  const [view, setView] = useState("industries"); // 'industries' or 'stocks'

  const handleIndustryClick = async (ind_code, industry) => {
    setView("stocks");
    setSelectedIndustry(industry);

    try {
      const { secrettoken, ts } = await getSecureToken();

      const res = await axios.get(
        `${API_BASE_URL}sector/get_stocks_by_industry.php?industry=${ind_code}`,
        {
          headers: {
            'X-TOKEN': secrettoken,
            'X-TIMESTAMP': ts,
          },
          withCredentials: true,
        }
      );

      setIndustryStocks(Array.isArray(res.data) ? res.data : []);
      // console.log("set industry", res.data);
    } catch (error) {
      console.error("Error fetching industry stocks:", error);
      setIndustryStocks([]);
    }
  };



  const [isSticky, setIsSticky] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("down");
  useEffect(() => {
    let lastScrollTop = 0;

    const handleScroll = () => {
      const currentScrollTop = window.scrollY;

      if (currentScrollTop > lastScrollTop && currentScrollTop > 200) {
        // Scrolling Down
        setIsSticky(true);
        setScrollDirection("down");
      } else if (currentScrollTop < lastScrollTop) {
        // Scrolling Up
        setIsSticky(false);
        setScrollDirection("up");
      }

      lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);





  return (
    <div className={style.chartContainer}>
      <div className={`${style.topsection} div d-flex justify-content-evenly`}>
        <div className={`${style.topsectionleft} col-9`}>
          <h2 className={style.chartTitle}> Industry Sector Analysis</h2>
          <div className={style.topsectionright}>
            {["all", "up", "down", "neutral"].map((t) => (
              <button
                key={t}
                className={`${style.filterBtn} ${trendFilter === t ? style.active : ""}`}
                onClick={() => filterDataByTrend(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}

          </div>

          <div className={` ${style.topSection}col-3`}>



            <Bar data={chartData} options={chartOptions} className={style.fullChartWrapper} />

          </div>

        </div>
        <div className={style.topSection}>
          <div className={style.card}>
            <h3 className={style.cardTitle}>🔥 Top % Changes</h3>
            <ul>
              {topChanges.map((i) => (
                <li key={i.industry}>
                  <strong>{i.industry}</strong>: {i.changePercentage.toFixed(2)}%
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>


      {loading ? (
        <div className={style.loader}></div>
      ) : error ? (
        <p className={style.error}>{error}</p>
      ) : (
        <div className={style.fadeIn}>

          <input
            type="text"
            placeholder="Search Industry..."
            value={industrySearch}
            onChange={(e) => setIndustrySearch(e.target.value)}
            className={style.searchInput}
          />

          <div className={style.slideContainer}>

            {/* Industry list */}
            <div className={`${style.view} ${view === "industries" ? style.activeView : style.inactiveView}`}>

              <div className={style.tableWrapper}>
                <table className={style.dataTable}>
                  <thead>
                    <tr>
                      <th>Industry</th>
                      <th>Avg. Open</th>
                      <th>Avg. Close</th>
                      <th>Avg. High</th>
                      <th>Avg. Low</th>
                      <th>
                        Change (%)
                        <span
                          onClick={() => filterDataByTrend("up")}
                          style={{ cursor: "pointer", color: trendFilter === "up" ? "green" : "gray", marginLeft: 5 }}
                          title="Show Top Gainers"
                        >
                          ▲
                        </span>
                        <span
                          onClick={() => filterDataByTrend("down")}
                          style={{ cursor: "pointer", color: trendFilter === "down" ? "red" : "gray", marginLeft: 5 }}
                          title="Show Top Losers"
                        >
                          ▼
                        </span>
                      </th>

                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData
                      .filter((i) =>
                        i.industry.toLowerCase().includes(industrySearch.toLowerCase())
                      )
                      .map((i, idx) => (
                        <tr key={idx}>
                          <td onClick={() => handleIndustryClick(i.ind_code, i.industry)} className={style.clickable}>
                            {i.industry}
                          </td>

                          <td>{formatNumber(i.avgOpen)}</td>
                          <td>{formatNumber(i.avgClose)}</td>
                          <td>{formatNumber(i.avgHigh)}</td>
                          <td>{formatNumber(i.avgLow)}</td>
                          <td>{formatNumber(i.changePercentage)}</td>
                          <td>
                            <span className={`${style.trend} ${style[i.trend]}`}>
                              {i.trend.charAt(0).toUpperCase() + i.trend.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>

                </table>
              </div>

            </div>

            {/* Stock list */}
            <div className={`${style.view} ${view === "stocks" ? style.activeView : style.inactiveView}`}>
              <button className={style.backButton} onClick={() => {
                setView("industries");
                setIndustryStocks([]);
                setSelectedIndustry(null);
              }}>← Back</button>
              <h3>{selectedIndustry}</h3>
              <div className={style.tableWrapper}>
                <table className={style.dataTable}>
                  <thead>
                    <tr>
                      <th>INDUSTRY</th>
                      <th>Stock Name</th>
                      <th>SYMBOL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {industryStocks.map((stock, index) => (
                      <tr key={index}>
                        <td>{stock.industry}</td>
                        <td>{stock.companyname}</td>
                        <td>{stock.symbol}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default IndustryChart;
